/**
 * InterestDashboard Component
 * 
 * Admin dashboard for managing presential course interest records.
 * Displays courses with interest counts, students needed for next group,
 * and allows filtering by course and status.
 * 
 * Requirements: 8.1, 8.2, 8.3
 */

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import {
  Users,
  BookOpen,
  RefreshCw,
  AlertCircle,
  Download,
  Filter,
  ChevronRight,
  UserCheck,
  Clock,
  XCircle,
  Link2
} from 'lucide-react';
import { getInterests, exportInterestsCSV } from '../services/courseInterestApi';
import InterestStudentList from './InterestStudentList';
import StandalonePaymentLinks from './StandalonePaymentLinks';

// Presential courses configuration - IDs must match CatalogoCursos.jsx
const PRESENTIAL_COURSES = [
  { id: 103, name: 'Uso y Manejo de Extintores' },
  { id: 101, name: 'Bastón y Esposas' },
  { id: 102, name: 'Defensa Personal y Técnicas de Reducción' },
];

const MINIMUM_QUOTA = 4;

/**
 * InterestDashboard Component
 * 
 * @param {string} className - Additional CSS classes
 */
export default function InterestDashboard({ className = '' }) {
  // Tab state
  const [activeTab, setActiveTab] = useState('interests');

  // Data state
  const [interests, setInterests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Selected course for detail view
  const [detailCourse, setDetailCourse] = useState(null);
  
  // Export state
  const [exporting, setExporting] = useState(false);

  /**
   * Fetch interest records from API
   */
  const fetchInterests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filters = {};
      if (selectedCourseId) filters.courseId = selectedCourseId;
      if (selectedStatus) filters.status = selectedStatus;
      
      const result = await getInterests(filters);
      setInterests(result.interests || []);
      setSummary(result.summary || null);
    } catch (err) {
      console.error('Failed to fetch interests:', err);
      setError(err.message || 'Error al cargar los registros de interés');
    } finally {
      setLoading(false);
    }
  }, [selectedCourseId, selectedStatus]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchInterests();
  }, [fetchInterests]);

  /**
   * Handle CSV export
   */
  const handleExport = async () => {
    setExporting(true);
    try {
      const filters = {};
      if (selectedCourseId) filters.courseId = selectedCourseId;
      if (selectedStatus) filters.status = selectedStatus;
      
      await exportInterestsCSV(filters);
    } catch (err) {
      console.error('Export failed:', err);
      setError(err.message || 'Error al exportar los registros');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Handle course selection for detail view
   */
  const handleCourseSelect = (course) => {
    setDetailCourse(course);
    setSelectedCourseId(course.id);
  };

  /**
   * Handle back to overview
   */
  const handleBackToOverview = () => {
    setDetailCourse(null);
    setSelectedCourseId(null);
  };

  /**
   * Calculate course statistics from interests
   */
  const getCourseStats = (courseId) => {
    const courseInterests = interests.filter(i => i.courseId === courseId);
    const pending = courseInterests.filter(i => i.status === 'pending').length;
    const confirmed = courseInterests.filter(i => i.status === 'confirmed').length;
    const cancelled = courseInterests.filter(i => i.status === 'cancelled').length;
    const paid = courseInterests.filter(i => i.paymentStatus === 'paid').length;
    const unnotified = courseInterests.filter(i => !i.notified).length;
    const studentsNeeded = MINIMUM_QUOTA - (unnotified % MINIMUM_QUOTA);
    const groups = [...new Set(courseInterests.filter(i => i.groupNumber).map(i => i.groupNumber))];
    
    return {
      total: courseInterests.length,
      pending,
      confirmed,
      cancelled,
      paid,
      unnotified,
      studentsNeeded: studentsNeeded === MINIMUM_QUOTA ? 0 : studentsNeeded,
      groupCount: groups.length,
      groups
    };
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  // If showing detail view for a specific course
  if (detailCourse && activeTab === 'interests') {
    return (
      <InterestStudentList
        courseId={detailCourse.id}
        courseName={detailCourse.name}
        onBack={handleBackToOverview}
        onRefresh={fetchInterests}
        className={className}
      />
    );
  }

  return (
    <div className={`${className}`}>
      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 bg-white/5 p-1 rounded-xl border border-white/10 w-fit">
        <button
          onClick={() => setActiveTab('interests')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'interests'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'text-white/50 hover:text-white/80 hover:bg-white/5 border border-transparent'
          }`}
        >
          <Users className="w-4 h-4" />
          Registro de Intereses
        </button>
        <button
          onClick={() => setActiveTab('links')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'links'
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
              : 'text-white/50 hover:text-white/80 hover:bg-white/5 border border-transparent'
          }`}
        >
          <Link2 className="w-4 h-4" />
          Generador de Enlaces
        </button>
      </div>

      {/* Standalone Payment Links Tab */}
      {activeTab === 'links' && (
        <StandalonePaymentLinks />
      )}

      {/* Interests Tab */}
      {activeTab === 'interests' && <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-400" />
            Interés en Cursos Presenciales
          </h2>
          <p className="text-white/60 text-sm mt-1">
            Gestiona los registros de interés para cursos con cupo mínimo
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Export Button */}
          <motion.button
            onClick={handleExport}
            disabled={exporting || loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 
                       text-emerald-400 rounded-lg border border-emerald-500/30
                       flex items-center gap-2 text-sm font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-bounce' : ''}`} />
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </motion.button>
          
          {/* Refresh Button */}
          <motion.button
            onClick={fetchInterests}
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 
                       text-white/80 rounded-lg border border-white/10
                       flex items-center gap-2 text-sm font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
        <Filter className="w-4 h-4 text-white/50" />
        <span className="text-sm text-white/50">Filtros:</span>
        
        {/* Status Filter */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg
                     text-white text-sm focus:outline-none focus:ring-2 
                     focus:ring-blue-500/50 cursor-pointer"
        >
          <option value="">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="confirmed">Confirmado</option>
          <option value="cancelled">Cancelado</option>
        </select>
        
        {/* Clear Filters */}
        {selectedStatus && (
          <button
            onClick={() => setSelectedStatus('')}
            className="text-xs text-white/50 hover:text-white/80 underline"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4" />
          <p className="text-white/60">Cargando registros de interés...</p>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-16 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-4 rounded-full bg-red-500/20 mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            Error al cargar los datos
          </h3>
          <p className="text-white/60 mb-6 max-w-md">{error}</p>
          <motion.button
            onClick={fetchInterests}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 
                       hover:from-blue-600 hover:to-cyan-600
                       text-white font-semibold rounded-xl
                       shadow-lg shadow-blue-500/30 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Reintentar
          </motion.button>
        </motion.div>
      )}

      {/* Course Cards Grid */}
      {!loading && !error && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {PRESENTIAL_COURSES.map((course) => {
            const stats = getCourseStats(course.id);
            
            return (
              <motion.div
                key={course.id}
                variants={itemVariants}
                onClick={() => handleCourseSelect(course)}
                className="p-5 rounded-xl cursor-pointer
                           bg-white/5 border border-white/10 backdrop-blur-sm
                           hover:bg-white/10 hover:border-white/20
                           transition-all duration-300"
                whileHover={{ y: -4, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {/* Course Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <BookOpen className="w-5 h-5 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white line-clamp-2">
                      {course.name}
                    </h3>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {/* Total Interested */}
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <Users className="w-4 h-4 text-cyan-400" />
                    <div>
                      <p className="text-xs text-white/50">Interesados</p>
                      <p className="text-sm font-semibold text-white">
                        {stats.total}
                      </p>
                    </div>
                  </div>

                  {/* Groups Formed */}
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <UserCheck className="w-4 h-4 text-emerald-400" />
                    <div>
                      <p className="text-xs text-white/50">Grupos</p>
                      <p className="text-sm font-semibold text-white">
                        {stats.groupCount}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Students Needed Indicator */}
                {stats.unnotified > 0 && (
                  <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      <span className="text-sm text-amber-400">
                        {stats.studentsNeeded > 0 
                          ? `Faltan ${stats.studentsNeeded} para completar grupo`
                          : '¡Grupo listo para notificar!'
                        }
                      </span>
                    </div>
                    <div className="mt-2 flex gap-1">
                      {[...Array(MINIMUM_QUOTA)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full ${
                            i < (stats.unnotified % MINIMUM_QUOTA || MINIMUM_QUOTA)
                              ? 'bg-amber-400'
                              : 'bg-white/10'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Summary */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">
                    <Clock className="w-3 h-3" />
                    {stats.pending}
                  </span>
                  <span className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/20 text-emerald-400">
                    <UserCheck className="w-3 h-3" />
                    {stats.confirmed}
                  </span>
                  <span className="flex items-center gap-1 px-2 py-1 rounded bg-red-500/20 text-red-400">
                    <XCircle className="w-3 h-3" />
                    {stats.cancelled}
                  </span>
                </div>

                {/* View Details Link */}
                <div className="flex items-center justify-end mt-4">
                  <span className="text-xs text-blue-400 flex items-center gap-1">
                    Ver estudiantes
                    <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Empty State */}
      {!loading && !error && interests.length === 0 && (
        <motion.div
          className="flex flex-col items-center justify-center py-16 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-4 rounded-full bg-blue-500/20 mb-4">
            <Users className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            No hay registros de interés
          </h3>
          <p className="text-white/60 max-w-md">
            Aún no hay estudiantes interesados en los cursos presenciales.
          </p>
        </motion.div>
      )}
      </>}
    </div>
  );
}

InterestDashboard.propTypes = {
  className: PropTypes.string,
};
